import MiniSearch from 'minisearch'
import { tokenize, tokenizeQuery } from './textNormalize'

// Build memoised MiniSearch indices from Redux-derived collections.
//
// MiniSearch is a small in-memory full-text engine that uses BM25 scoring
// over a configurable per-token analyser. Token-level scoring is a much
// closer behavioural match to Algolia than character-level fuzzy matching,
// which is why it is the only local engine left; the two are compared
// against Algolia at runtime through `searchReporter`.
//
// Each MiniSearch instance is shaped to mirror the corresponding Algolia
// index — `repos`, `folders`, `sets_questions` — so the same UI primitives
// (`IndexHitList` / `SectionHeader` / per-index totals) can render results
// from any of the three engines without branching.
//
// Hit shape is the original Redux record spread + the standard
// `__localOrigin: true` + `objectID` markers used by all local providers,
// plus a `__localEngine: 'minisearch'` discriminator that the UI reads to
// render a distinct badge variant.

// ─── Tokenization & analyser ───────────────────────────────────────────────

// `tokenize` is the shared Unicode-aware tokenizer (diacritic folding + CJK /
// no-space-script segmentation). MiniSearch applies it to BOTH documents and
// queries, so both sides stay symmetric. Because folding already happens
// inside `tokenize`, `processTerm` only needs to drop empty tokens.
function processTerm(term) {
  if (typeof term !== 'string') return false
  return term.length > 0 ? term : false
}

// ─── Search profiles ───────────────────────────────────────────────────────
//
// Two named profiles run over the SAME indices. MiniSearch accepts a full set
// of search options per `search()` call, so the tuned profile costs one extra
// in-memory BM25 pass over data that is already loaded — no second index, no
// extra memory for documents. Both profiles are reported on every query, which
// makes the comparison paired: same query, same library, same user, same
// moment. See SEARCH_QUALITY_ANALYTICS.md Q21.
export const SEARCH_PROFILES = { BASELINE: 'baseline', TUNED: 'tuned' }

// Bump whenever the tuned option values below change. Reported with every
// quality report so rows recorded under different tuning iterations can be told
// apart in Redshift — otherwise a second tuning pass silently pools its results
// with the first one's.
export const TUNED_PROFILE_VERSION = 'tuned-v1'

// Non-global so `.test()` stays stateless.
const HAS_DIGIT = /\d/

// ─── Baseline profile (unchanged behaviour) ────────────────────────────────
//
// `prefix: true`  — described in the original code as the Algolia `prefixLast`
//                   equivalent, but it is not: Algolia prefix-matches only the
//                   LAST token, this matches every token. Telemetry shows the
//                   cost — a single-character token prefix-matches most of the
//                   library, which is how `M` returned 42 distinct titles.
// `fuzzy: 0.2`    — Levenshtein tolerance ~20% of token length, applied to
//                   numeric tokens too. `round(0.2 * 4) = 1` edit, so `mr17`
//                   matches MR10 / MR14 / MR16 / MR18.
// `combineWith`   — 'AND' so multi-token queries require every token to
//                   match somewhere in the document (Algolia default).
//
// Kept exactly as-is: it is the control arm of the experiment.
const BASELINE_SEARCH_OPTIONS = {
  prefix: true,
  fuzzy: 0.2,
  combineWith: 'AND',
}

// ─── Tuned profile ─────────────────────────────────────────────────────────
//
// Mirrors Algolia's actual typo configuration rather than the baseline's
// approximation of it: `minWordSizefor1Typo: 4`, `minWordSizefor2Typos: 8`,
// prefix on the last token only, and no typo tolerance on tokens containing
// digits (Algolia treats numbers as exact by default).
//
// Both the excess hits and the misses trace back to these two knobs. At TopNav,
// where only four results are shown, a "missing" hit is usually a correct
// result pushed out of the top four by fuzzy and prefix junk — which is why
// 70% of missed titles contain digits. One root cause, two symptoms.
//
// `weights` lowers the score contribution of fuzzy and prefix matches relative
// to exact ones (MiniSearch defaults are 0.45 / 0.375), so an exact match on a
// less-boosted field still outranks an approximate match on a boosted one.
const TUNED_SEARCH_OPTIONS = {
  combineWith: 'AND',
  prefix: (term, index, terms) => (
    index === terms.length - 1 && term.length >= 3 && !HAS_DIGIT.test(term)
  ),
  fuzzy: (term) => {
    if (HAS_DIGIT.test(term)) return false
    if (term.length >= 8) return 0.25
    if (term.length >= 4) return 0.2
    return false
  },
  weights: { fuzzy: 0.2, prefix: 0.3 },
  // Query side only — documents keep their n-grams. See tokenizeQuery.
  tokenize: tokenizeQuery,
}

// Drop tuned hits scoring below this fraction of the top hit's score.
//
// BM25 already ranks the junk last; the problem is that the caller slices a
// fixed number of hits off the top and reports everything it took. A relative
// floor cuts the long tail before the slice, so a query with two strong matches
// reports two hits rather than padding to the cap with noise. Relative rather
// than absolute because BM25 scores are not comparable across queries.
const TUNED_SCORE_FLOOR_RATIO = 0.25

// ─── Per-domain index builders ─────────────────────────────────────────────
//
// Each builder produces an independent MiniSearch instance with field
// boosts that mirror `TEMP_LOCAL_SEARCH_INDEXES.md`. The `_kind` field
// labels every document so the unified `sets_questions` index can keep
// the set/question discriminator implicit (matches Algolia's behaviour).
// `_original` carries the source Redux record back into the hit; this
// trades memory for code simplicity and avoids a parallel id→record map.

function notArchived(item) {
  return item && !item.archived && !item.parentArchived
}

function joinNonEmpty(arrayOfStrings) {
  if (!Array.isArray(arrayOfStrings)) return ''
  return arrayOfStrings.filter((s) => typeof s === 'string' && s.length > 0).join(' ')
}

function buildRepoDocs(repos) {
  return repos.filter(notArchived).map((repo) => {
    const author = repo.author || {}
    return {
      msId: `repo-${repo.id}`,
      name: repo.name,
      description: repo.description,
      authorFirstName: author.firstName,
      authorLastName: author.lastName,
      _kind: 'repo',
      _original: repo,
    }
  })
}

function buildFolderDocs(folders) {
  return folders.filter(notArchived).map((folder) => ({
    msId: `folder-${folder.id}`,
    name: folder.name,
    _kind: 'folder',
    _original: folder,
  }))
}

function buildSetDocs(sets) {
  return sets.filter(notArchived).map((set) => {
    const questions = Array.isArray(set.questions) ? set.questions : []
    const bodies = questions.map((q) => (q ? q.body : ''))
    const choices = []
    for (let i = 0; i < questions.length; i += 1) {
      const q = questions[i]
      if (q && Array.isArray(q.choices)) {
        for (let j = 0; j < q.choices.length; j += 1) {
          const c = q.choices[j]
          if (c && typeof c.body === 'string') choices.push(c.body)
        }
      }
    }
    return {
      msId: `set-${set.id}`,
      name: set.name,
      questionBodies: joinNonEmpty(bodies),
      questionChoices: joinNonEmpty(choices),
      _kind: 'set',
      _original: set,
    }
  })
}

function buildQuestionDocs(questions) {
  return questions.filter(notArchived).map((question) => {
    const choices = Array.isArray(question.choices) ? question.choices : []
    const choiceBodies = choices.map((c) => (c ? c.body : ''))
    return {
      msId: `question-${question.id}`,
      body: question.body,
      choices: joinNonEmpty(choiceBodies),
      _kind: 'question',
      _original: question,
    }
  })
}

// ─── Per-profile field boosts ──────────────────────────────────────────────
//
// Only `setsQuestions` differs between the profiles, deliberately: it is the
// index carrying nearly all the traffic, and keeping repos/folders identical
// means any movement there cannot be attributed to a boost change.
//
// The baseline weights body text (`questionBodies: 2` against `name: 3`) high
// enough that a word appearing in ANY question of a set matches the set — the
// reason a query for `Proteins` returns "An alpha particle consists of …", and
// why `math`, `name` and `reading` each return 20+ unrelated sets. The tuned
// profile widens the gap between the title and the body fields instead of
// removing body matching, so a title match wins whenever one exists.
const BASELINE_BOOSTS = {
  repos: {
    name: 3, description: 1, authorFirstName: 1, authorLastName: 1,
  },
  folders: { name: 1 },
  setsQuestions: {
    name: 3, questionBodies: 2, questionChoices: 1, body: 3, choices: 1,
  },
}

const TUNED_BOOSTS = {
  repos: BASELINE_BOOSTS.repos,
  folders: BASELINE_BOOSTS.folders,
  setsQuestions: {
    name: 6, questionBodies: 1, questionChoices: 0.5, body: 4, choices: 0.5,
  },
}

const REPOS_OPTIONS = {
  idField: 'msId',
  fields: ['name', 'description', 'authorFirstName', 'authorLastName'],
  storeFields: ['_kind', '_original'],
  tokenize,
  processTerm,
  searchOptions: { ...BASELINE_SEARCH_OPTIONS, boost: BASELINE_BOOSTS.repos },
}

const FOLDERS_OPTIONS = {
  idField: 'msId',
  fields: ['name'],
  storeFields: ['_kind', '_original'],
  tokenize,
  processTerm,
  searchOptions: { ...BASELINE_SEARCH_OPTIONS, boost: BASELINE_BOOSTS.folders },
}

const SETS_QUESTIONS_OPTIONS = {
  idField: 'msId',
  fields: [
    'name', 'questionBodies', 'questionChoices',
    'body', 'choices',
  ],
  storeFields: ['_kind', '_original'],
  tokenize,
  processTerm,
  searchOptions: { ...BASELINE_SEARCH_OPTIONS, boost: BASELINE_BOOSTS.setsQuestions },
}

// ─── Memoisation cache ─────────────────────────────────────────────────────
//
// Keyed on the input list reference. Redux replaces the array reference
// whenever any item changes; the cache returns the same MiniSearch instance
// until that happens.

const reposCache = new WeakMap()
const foldersCache = new WeakMap()
const setsQuestionsCache = new WeakMap()

function getRepoIndex(repos) {
  const list = Array.isArray(repos) ? repos : []
  const cached = reposCache.get(list)
  if (cached) return cached
  const ms = new MiniSearch(REPOS_OPTIONS)
  ms.addAll(buildRepoDocs(list))
  reposCache.set(list, ms)
  return ms
}

function getFolderIndex(folders) {
  const list = Array.isArray(folders) ? folders : []
  const cached = foldersCache.get(list)
  if (cached) return cached
  const ms = new MiniSearch(FOLDERS_OPTIONS)
  ms.addAll(buildFolderDocs(list))
  foldersCache.set(list, ms)
  return ms
}

function getSetsQuestionsIndex(sets, questions) {
  const setsList = Array.isArray(sets) ? sets : []
  const questionsList = Array.isArray(questions) ? questions : []
  const cached = setsQuestionsCache.get(setsList)
  if (cached && cached.questions === questionsList) return cached.ms
  const ms = new MiniSearch(SETS_QUESTIONS_OPTIONS)
  ms.addAll([...buildSetDocs(setsList), ...buildQuestionDocs(questionsList)])
  setsQuestionsCache.set(setsList, { questions: questionsList, ms })
  return ms
}

/**
 * Build per-domain MiniSearch indices from Redux collections.
 *
 * @returns {{ repos: MiniSearch, folders: MiniSearch, setsQuestions: MiniSearch }}
 */
export function buildMiniSearchIndices({
  sets, questions, folders, repos,
}) {
  return {
    repos: getRepoIndex(repos),
    folders: getFolderIndex(folders),
    setsQuestions: getSetsQuestionsIndex(sets, questions),
  }
}

/**
 * Per-index document counts for a built index set.
 *
 * Reported alongside every search-quality report. Both local engines only ever
 * index what Redux has loaded, so without this a "missing hit" is ambiguous
 * between *the tokenizer failed to match it* and *the document was never in the
 * local index at all*. Q18 in SEARCH_QUALITY_ANALYTICS.md separates the two.
 *
 * @param {ReturnType<typeof buildMiniSearchIndices>} indices
 * @returns {{ repos: number, folders: number, sets_questions: number }}
 */
export function miniSearchIndexSizes(indices) {
  const count = (ms) => ((ms && typeof ms.documentCount === 'number') ? ms.documentCount : 0)
  const set = indices || {}
  return {
    repos: count(set.repos),
    folders: count(set.folders),
    // eslint-disable-next-line camelcase
    sets_questions: count(set.setsQuestions),
  }
}

// Result-object index names to the MiniSearch instances they were produced from.
const INDEX_INSTANCE_KEY = {
  repos: 'repos',
  folders: 'folders',
  // eslint-disable-next-line camelcase
  sets_questions: 'setsQuestions',
}

/**
 * Was this document in the local index at all?
 *
 * Index sizes answer the question for a whole collection; this answers it for
 * one document, which is what a single missing hit needs. A document that IS
 * indexed but did not come back is a matching or ranking failure and belongs in
 * the tuning backlog. A document that was never indexed is a loading failure and
 * no amount of tuning will find it.
 *
 * @param {ReturnType<typeof buildMiniSearchIndices>} indices
 * @param {('repos'|'folders'|'sets_questions')} indexName
 * @param {('repo'|'folder'|'set'|'question'|null)} documentType from getDocumentType
 * @param {string} objectID
 * @returns {boolean|null} null when the document could not be identified, which
 *   must not be read as `false` — an unrecognised record shape is not a missing one
 */
export function miniSearchHasDocument(indices, indexName, documentType, objectID) {
  if (!indices || !documentType || !objectID) return null
  const ms = indices[INDEX_INSTANCE_KEY[indexName]]
  if (!ms || typeof ms.has !== 'function') return null
  return ms.has(`${documentType}-${objectID}`)
}

// ─── Hit projection ────────────────────────────────────────────────────────

function toHit(result, profile) {
  const original = result._original || {}
  return {
    ...original,
    objectID: original.id,
    __localOrigin: true,
    __localEngine: 'minisearch',
    __minisearchOrigin: true,
    __minisearchProfile: profile || SEARCH_PROFILES.BASELINE,
    __minisearchScore: result.score,
    // MiniSearch returns `terms: ['math']` and `match: { math: ['name'] }`.
    // Both are kept on the hit so the search-tuning sandbox can show which
    // query term matched which field.
    __minisearchTerms: result.terms,
    __minisearchMatch: result.match,
  }
}

const NOOP = () => {}

// Per-index hit cap applied when the caller does not pass one. Exported so the
// orchestrator can report the cap actually in force alongside each quality
// report — a row that returned exactly the cap is measuring the cap, not recall.
export const DEFAULT_LIMIT_PER_INDEX = 10

function emptyIndexResult() {
  return {
    hits: [], hitCount: 0, hasMore: false, loadMore: NOOP,
  }
}

// Resolve the search options for one index under one profile. The baseline
// values match the constructor `searchOptions` exactly, so passing them
// explicitly changes nothing — it just puts both profiles on the same footing.
function profileSearchOptions(profile, indexKey) {
  const isTuned = profile === SEARCH_PROFILES.TUNED
  return {
    ...(isTuned ? TUNED_SEARCH_OPTIONS : BASELINE_SEARCH_OPTIONS),
    boost: (isTuned ? TUNED_BOOSTS : BASELINE_BOOSTS)[indexKey],
  }
}

// MiniSearch returns results already sorted by descending score, so the top hit
// sets the bar for the rest. Baseline is left untouched — it is the control arm.
function applyScoreFloor(results, profile) {
  if (profile !== SEARCH_PROFILES.TUNED || results.length === 0) return results
  const floor = results[0].score * TUNED_SCORE_FLOOR_RATIO
  return results.filter((r) => r.score >= floor)
}

function runIndex(ms, query, limit, profile, indexKey) {
  if (!query || !ms) return emptyIndexResult()
  const found = ms.search(query, profileSearchOptions(profile, indexKey))
  const all = applyScoreFloor(found, profile)
  const hitCount = all.length
  const capped = (typeof limit === 'number' && limit > 0) ? all.slice(0, limit) : all
  return {
    hits: capped.map((result) => toHit(result, profile)),
    hitCount,
    // Everything is already in memory, so "more" simply means the cap cut the
    // list short. The provider supplies the `loadMore` that raises the cap —
    // this module has no state to page through.
    hasMore: capped.length < hitCount,
    loadMore: NOOP,
  }
}

// `limitPerIndex` is either one number for every index, or a per-index map when
// the caller pages indexes independently (Show more raises one index's cap
// without disturbing the others).
function resolveLimit(limitPerIndex, resultKey) {
  if (limitPerIndex && typeof limitPerIndex === 'object') {
    const limit = limitPerIndex[resultKey]
    return typeof limit === 'number' ? limit : DEFAULT_LIMIT_PER_INDEX
  }
  return limitPerIndex
}

/**
 * Run a query against every MiniSearch index and return per-index results
 * in the same shape as `AlgoliaSearchProvider`'s `indexResults`.
 *
 * `hitCount` is the true uncapped match count while `hits` is capped at
 * `limitPerIndex`.
 *
 * @param {ReturnType<typeof buildMiniSearchIndices>} indices
 * @param {string} query
 * @param {number|{repos: number, folders: number, sets_questions: number}} [limitPerIndex=10]
 * @param {('baseline'|'tuned')} [profile='baseline']
 */
export function searchPerMiniSearchIndex(
  indices,
  query,
  limitPerIndex = DEFAULT_LIMIT_PER_INDEX,
  profile = SEARCH_PROFILES.BASELINE,
) {
  const limitFor = (resultKey) => resolveLimit(limitPerIndex, resultKey)
  return {
    repos: runIndex(indices.repos, query, limitFor('repos'), profile, 'repos'),
    folders: runIndex(indices.folders, query, limitFor('folders'), profile, 'folders'),
    // eslint-disable-next-line camelcase
    sets_questions: runIndex(
      indices.setsQuestions,
      query,
      limitFor('sets_questions'),
      profile,
      'setsQuestions',
    ),
  }
}
